import '@root/app.scss';

import App from '@components/App';
import { Api } from '@config/api';
import { configToContext } from '@config/configToContext';
import { NODE_ENV } from '@config/env';
import { ConfigProvider } from '@context/ConfigContext';
import {
  DEFAULT_CHAT_WINDOW_FONT_FAMILY,
  type TClientOptions,
} from '@conversed/shared/src/central/types/webclient-config.type';
import { renderRootForContainerId } from '@conversed/ui/src/helpers/render';
import {
  getConfigToUse,
  getConfigToUseFromQueryString,
} from '@services/configService';
import * as eventHandlerService from '@services/eventHandlerService';
import geoLocationService from '@services/geolocationService';
import { createHTTPInstance } from '@services/httpService';
import languageService from '@services/languageService';
import localStorageService, {
  getStorage,
  patchStorage,
} from '@services/localStorageService';
import {
  type SSOAuthResponse,
  sendLogMessage,
} from '@services/postMessageService';
import { storeBypassToken } from '@services/ssoBypassService';
import type { Config, Metadata } from '@type/configs';
import type { Location, LocationError } from '@type/location';
import { useCallback, useMemo, useState } from 'react';

const WebclientRoot: React.FC<{ config: Config; configId: string }> = ({
  config,
  configId,
}) => {
  const [activeLanguage, setActiveLanguage] = useState(
    () => getStorage(configId).language || config.options.language || '',
  );

  // The stored language belongs to a session. When that session is gone the
  // widget starts from scratch, so the language goes back to the config default
  // instead of leaving the chrome in a language the new conversation is not in.
  const handleLanguageReset = useCallback(() => {
    const defaultLanguage = config.options.language || '';

    setActiveLanguage(defaultLanguage);
    patchStorage(configId, { language: '' });
    languageService.registerLanguage(defaultLanguage);
  }, [config, configId]);

  const handleLanguageChange = useCallback(
    (language: string) => {
      if (!language) {
        return;
      }

      setActiveLanguage(language);
      patchStorage(configId, { language });
      // Keeps the emoji picker's locale in step with the conversation.
      languageService.registerLanguage(language);
    },
    [configId],
  );

  const contextValue = useMemo(
    () => configToContext(config, activeLanguage),
    [config, activeLanguage],
  );

  return (
    <ConfigProvider value={contextValue}>
      <App
        config={config}
        onLanguageChange={handleLanguageChange}
        onLanguageReset={handleLanguageReset}
      />
    </ConfigProvider>
  );
};

const applyFontConfiguration = (fonts: { chatWindow?: string }) => {
  let fontFamily = fonts?.chatWindow || DEFAULT_CHAT_WINDOW_FONT_FAMILY;

  // Always ensure Arial is present as fallback
  // This is necessary for proper font rendering in all browsers and OS combinations
  if (!fontFamily.includes('Arial')) {
    fontFamily += ', Arial, sans-serif';
  }

  document.documentElement.style.setProperty(
    '--chat-font-family',
    `${fontFamily}`,
  );

  sendLogMessage(`Applied font configuration: ${fontFamily}`, 'info');
};

const render = (config: Config | null) => {
  const URL = Api.CONVERSED_SERVER;

  if (!config?.options || !config?.scheme) {
    sendLogMessage(
      'Initial config not available for webclient, exiting early',
      'error',
    );
    return;
  }

  const configToContextValue = configToContext(
    config,
    config.options.language || '',
  );
  applyFontConfiguration(configToContextValue.fonts);

  if (config?.isEnabled === false) {
    sendLogMessage('Bot configuration is not enabled, exiting early', 'error');
    return;
  }

  if (!configToContextValue?.botId) {
    sendLogMessage(
      'Failed to load webclient. Invalid config found: botId is required',
      'error',
    );
    return;
  }

  if (!configToContextValue?.configId) {
    sendLogMessage(
      'Failed to load webclient. Invalid config found: configId is required',
      'error',
    );
    return;
  }

  if (config?.shouldReInitChatSession) {
    sendLogMessage('Reinitializing chat session due to idle timeout', 'info');
    localStorageService.delete(configToContextValue?.configId);
  }

  createHTTPInstance(URL, configToContextValue.botId);

  renderRootForContainerId(
    'conversed-chat-widget-1848',
    <WebclientRoot config={config} configId={configToContextValue.configId} />,
  );
};

type EventHandlerParams = {
  CONVERSED_READY: {
    config?: Config;
    configId: string;
    metadata: Metadata;
    clientOptions?: TClientOptions;
  };
  CONVERSED_LOCATION: {
    location: Location;
    error: LocationError;
    configId: string;
  };
  CONVERSED_EVENT: {
    type: 'new-webclient';
    data: { id: string; requestSenderId?: string };
  };
  CONVERSED_SSO_RESPONSE: {
    payload: SSOAuthResponse;
  };
};

const eventHandlers = {
  CONVERSED_READY: async ({
    config,
    configId,
    metadata,
    clientOptions,
  }: EventHandlerParams['CONVERSED_READY']) => {
    if (typeof clientOptions?.ssoToken === 'string') {
      storeBypassToken(clientOptions.ssoToken);
    }
    const configToUse = await getConfigToUse({
      configFromScript: config,
      configId,
      metadata,
    });
    return render(configToUse);
  },
  CONVERSED_LOCATION: ({
    configId,
    location,
    error,
  }: EventHandlerParams['CONVERSED_LOCATION']) =>
    location?.longitude && location?.latitude
      ? geoLocationService.sendLocation(location, configId)
      : geoLocationService.sendLocationError(error, configId),
  CONVERSED_EVENT: ({ type, data }: EventHandlerParams['CONVERSED_EVENT']) =>
    eventHandlerService.receiveBroadcast(type, data),
  CONVERSED_SSO_RESPONSE: ({
    payload,
  }: EventHandlerParams['CONVERSED_SSO_RESPONSE']) => {
    // This event is handled by the sendSSOAuthRequest promise in postMessageService
    // The listener is set up there, so we don't need to do anything here
    // This handler is just for type safety and documentation
    sendLogMessage(
      `SSO response received: ${payload.success ? 'success' : 'failure'}`,
      'info',
    );
  },
};

const setupPostMessageListeners = () => {
  window.addEventListener('message', ({ data }: MessageEvent) => {
    // TODO get ALLOWED_ORIGINS from backend based on subdomain
    const handler = eventHandlers[data.eventType?.toString()];
    if (handler) {
      handler(data);
    }
  });
};

const init = (): unknown => {
  const isInIFrame = window.location !== window.parent.location;
  if (isInIFrame) {
    sendLogMessage('Loading chat interface', 'info');
    return setupPostMessageListeners();
  }

  const isInDevelopment = NODE_ENV !== 'production';
  if (isInDevelopment) {
    return render(getConfigToUseFromQueryString());
  }
  // eslint-disable-next-line no-console
  return console.error('This site must be loaded in IFRAME');
};

init();
